import { Box, Center, HStack, Text } from "@chakra-ui/layout";
import { Card, Spinner } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { BillingRow } from "@src/components/modules/projects/detail/Evaluation/Billing";
import { CardHeader } from "@src/components/modules/projects/detail/Stats/CardHeader";
import { currency } from "@src/utils/formatters";
import type { Prettify } from "@src/utils/types";
import { observer } from "mobx-react-lite";
import type { RevenueSummaryProps } from "../../summary/revenue";
import { PieList } from "../pie-list";

type Summary = NonNullable<RevenueSummaryProps["summary"]>;

type Props = Prettify<
  Omit<RevenueSummaryProps, "summary"> & {
    summary:
      | {
          total_invoiced: Summary["total_invoiced"];
          our_work_billings_for_date_range: Summary["our_work_billings_for_date_range"];
          expense_revenue_for_date_range: Summary["expense_revenue_for_date_range"];
          brand_revenue_share?: Summary["brand_revenue_share"];
          client_revenue_share?: Summary["client_revenue_share"];
        }
      | undefined;
    isResponsive?: boolean;
  }
>;

export const RevenueCard = observer(function RevenueCard({
  summary,
  isFetching,
  expenseBudgetCategories,
  isResponsive,
}: Props) {
  return (
    <Card p="4" bg="grey.50">
      {!summary || isFetching ? (
        <Center minH="32">
          <Spinner size="xl" />
        </Center>
      ) : (
        <HStack spacing="4" w="full" align="start">
          <Box w={isResponsive ? undefined : "full"}>
            <CardHeader title={t`Invoiced (total)`}>
              <Text fontSize="xl" fontWeight="bold">
                {currency.formatByWorkspace(summary.total_invoiced)}
              </Text>
            </CardHeader>
            <BillingRow
              title={t`Work`}
              value={currency.formatByWorkspace(
                summary.our_work_billings_for_date_range,
              )}
            />
            {expenseBudgetCategories.map((category) => {
              const revenue = summary.expense_revenue_for_date_range.find(
                (revenue) => revenue.category.id === category.id,
              );
              return revenue ? (
                <BillingRow
                  title={revenue.category.name}
                  value={currency.formatByWorkspace(revenue.billings)}
                  key={`revenue-${revenue.category.name}`}
                />
              ) : null;
            })}
          </Box>
          <PieList
            title={t`Share Invoiced (by Brand)`}
            data={summary.brand_revenue_share ?? []}
            outerRadius={40}
            isResponsive={isResponsive}
            pieHeight="28"
          />
          <PieList
            title={t`Share Invoiced (by Client)`}
            data={summary.client_revenue_share ?? []}
            outerRadius={40}
            isResponsive={isResponsive}
            pieHeight="28"
          />
        </HStack>
      )}
    </Card>
  );
});
